import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { isApiUrl, isPublicAuthUrl } from '@core/http/api-url';
import { AppConfigApiService } from '@core/services/api/app-config-api.service';
import { AuthStateService } from '@features/auth/services/auth-state.service';

@Injectable()
export class ApiHeadersInterceptor implements HttpInterceptor {
  private readonly configApi = inject(AppConfigApiService);
  private readonly authState = inject(AuthStateService);
  private readonly translate = inject(TranslateService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(request.url)) {
      return next.handle(request);
    }

    const headers: Record<string, string> = {
      'Accept-Language': toAcceptLanguage(this.translate.currentLang),
    };

    // JSON is the default, but a caller that asks for something else keeps it —
    // `user/avatar` answers with an image, not with a JSON envelope.
    if (!request.headers.has('Accept')) {
      headers['Accept'] = 'application/json';
    }

    const token = this.authState.token();
    if (token && !isPublicAuthUrl(request.url)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return next.handle(
      request.clone({
        setHeaders: headers,
      }),
    );
  }

  /**
   * The API's own origin, whole - not only what sits under the base path. A document
   * the payload files on the host (an ERI document, an LRI certificate PDF) is a
   * request of this session and needs the bearer token like any other; a link to
   * somebody else's host never gets one. Shared with `AuthExpiredInterceptor`, which
   * has to answer the 401s of exactly the requests that carry a token here.
   */
  private isApiRequest(url: string): boolean {
    return isApiUrl(url, this.configApi.getApiBaseUrl());
  }
}

/**
 * Maps the app locale (`pt-BR` / `en-US` / `es`) to the API Accept-Language
 * values (`pt_BR` / `en` / `es`). Anything else falls back to `en`.
 */
export function toAcceptLanguage(lang: string | null | undefined): string {
  switch (lang) {
    case 'pt-BR':
      return 'pt_BR';
    case 'en-US':
      return 'en';
    case 'es':
      return 'es';
    default:
      return 'en';
  }
}
